import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { CalendarColors } from '../calendar_colors'

export default class extends Controller {
  static targets = ["calendar", "eventsTitle", "eventsContent", "eventFilter"]
  static values = { canManageEvents: Boolean }

  connect() {
    this.selectedDate = null
    this.initializeCalendar()
    
    // Listen for filter changes
    this.eventFilterTargets.forEach(filter => {
      filter.addEventListener('change', this.handleFilterChange.bind(this))
    })
    
    // Listen for calendar refresh events
    this.boundRefresh = this.refresh.bind(this)
    this.element.addEventListener('calendar:refresh', this.boundRefresh)
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
    }
    
    // Clean up event listeners
    if (this.boundRefresh) {
      this.element.removeEventListener('calendar:refresh', this.boundRefresh)
    }
  }

  initializeCalendar() {
    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      headerToolbar: {
        start: 'title',
        center: '',
        end: 'prev,next'
      },
      height: 'auto',
      selectable: true,
      showNonCurrentDates: false,
      fixedWeekCount: false,

      dateClick: (info) => {
        // Handle date selection
        this.selectDate(info.dateStr)
      },
      datesSet: () => {
        // Replace icons when calendar navigates and add event indicators
        this.replaceNavigationIcons()
        this.addEventIndicators()
      },
      dayCellClassNames: (info) => {
        // Add custom classes for styling
        const today = new Date()
        const cellDate = new Date(info.date)
        
        if (cellDate.toDateString() === today.toDateString()) {
          return ['fc-today-custom']
        }
        return []
      }
    })
    
    this.calendar.render()
    
    // Wait for render and set up icons/indicators
    setTimeout(() => {
      this.replaceNavigationIcons()
      this.addEventIndicators()
    }, 100)
    setTimeout(() => {
      this.replaceNavigationIcons()
      this.addEventIndicators()
    }, 300)
    setTimeout(() => {
      this.replaceNavigationIcons()
      this.addEventIndicators()
    }, 500)
    
    // Initialize events section with today's events - wait for targets to be available
    setTimeout(() => {
      this.initializeEventsSection()
    }, 100)
  }

  async addEventIndicators() {
    // Get the current month view date
    const currentDate = this.calendar.getDate()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1 // JavaScript months are 0-indexed
    
    try {
      // Fetch events for the current month
      const response = await fetch(`/calendar/events?month=${month}&year=${year}`)
      const monthData = await response.json()
      
      // Clear previous event indicators
      this.calendarTarget.querySelectorAll('.fc-daygrid-day-number').forEach(dayNumber => {
        dayNumber.classList.remove('has-events', 'has-work', 'has-event', 'has-unavailability')
      })
      
      // Add indicators for days with events
      const dayNumbers = this.calendarTarget.querySelectorAll('.fc-daygrid-day-number')
      
      dayNumbers.forEach(dayNumber => {
        const dayText = dayNumber.textContent.trim()
        const dayNum = parseInt(dayText)
        const dayCell = dayNumber.closest('.fc-daygrid-day')
        
        if (dayCell && !dayCell.classList.contains('fc-day-other')) {
          // Check if this day has birthday events
          if (monthData.birthday_dates && monthData.birthday_dates.includes(dayNum)) {
            dayNumber.classList.add('has-events')
          }
          
          // Check if this day has work events
          if (monthData.work_dates && monthData.work_dates.includes(dayNum)) {
            dayNumber.classList.add('has-work')
          }
          
          // Check if this day has calendar events (by type)
          Object.keys(monthData).forEach(key => {
            if (key.endsWith('_dates') && (key === 'event_dates' || key === 'unavailability_dates')) {
              const eventType = key.replace('_dates', '')
              if (monthData[key] && monthData[key].includes(dayNum)) {
                dayNumber.classList.add(`has-${eventType}`)
              }
            }
          })
        }
      })
    } catch (error) {
      console.error('Failed to fetch event indicators:', error)
    }
  }

  replaceNavigationIcons() {
    // Small timeout to ensure DOM is ready
    setTimeout(() => {
      const prevButton = this.calendarTarget.querySelector('.fc-prev-button')
      const nextButton = this.calendarTarget.querySelector('.fc-next-button')
      
      if (prevButton) {
        // Clear existing content and add icon
        prevButton.innerHTML = ''
        const prevIcon = document.createElement('iconify-icon')
        prevIcon.setAttribute('icon', 'ph:caret-left')
        prevIcon.classList.add('text-lg')
        prevButton.appendChild(prevIcon)
        
        // Ensure button styling
        this.styleButton(prevButton)
      }
      
      if (nextButton) {
        // Clear existing content and add icon
        nextButton.innerHTML = ''
        const nextIcon = document.createElement('iconify-icon')
        nextIcon.setAttribute('icon', 'ph:caret-right')
        nextIcon.classList.add('text-lg')
        nextButton.appendChild(nextIcon)
        
        // Ensure button styling
        this.styleButton(nextButton)
      }
    }, 10)
  }

  styleButton(button) {
    button.style.display = 'flex'
    button.style.alignItems = 'center'
    button.style.justifyContent = 'center'
    button.style.padding = '0.5rem 1rem'
    button.style.minWidth = '2rem'
    button.style.minHeight = '2rem'
    button.style.borderRadius = '0.5rem'
    button.style.border = '1px solid hsl(var(--bc) / 0.2)'
    button.style.backgroundColor = 'hsl(var(--b1))'
    button.style.color = 'hsl(var(--bc))'
    button.style.transition = 'all 0.2s'
    
    button.addEventListener('mouseenter', () => {
      button.style.backgroundColor = 'hsl(var(--b2))'
    })
    
    button.addEventListener('mouseleave', () => {
      button.style.backgroundColor = 'hsl(var(--b1))'
    })
  }

  selectDate(dateString) {
    // Store the selected date
    this.selectedDate = dateString
    
    // Remove previous selection from all day numbers
    this.calendarTarget.querySelectorAll('.fc-daygrid-day-number').forEach(dayNumber => {
      dayNumber.classList.remove('selected-date')
    })
    
    // Find the exact date cell using FullCalendar's data-date attribute
    const targetDayCell = this.calendarTarget.querySelector(`[data-date="${dateString}"]`)
    
    if (targetDayCell) {
      const dayNumber = targetDayCell.querySelector('.fc-daygrid-day-number')
      if (dayNumber) {
        dayNumber.classList.add('selected-date')
      }
    } else {
      // Fallback method: find by matching date string more precisely
      const clickDate = new Date(dateString + 'T00:00:00') // Ensure proper date parsing
      const dayNumber = clickDate.getDate().toString()
      const month = clickDate.getMonth()
      const year = clickDate.getFullYear()
      
      // Get current view date to ensure we're in the right month
      const currentDate = this.calendar.getDate()
      
      if (currentDate.getMonth() === month && currentDate.getFullYear() === year) {
        // Find day number in current month only
        const dayNumbers = this.calendarTarget.querySelectorAll('.fc-daygrid-day-number')
        
        dayNumbers.forEach(element => {
          if (element.textContent.trim() === dayNumber) {
            const dayCell = element.closest('.fc-daygrid-day')
            // Make sure it's in current month (not fc-day-other)
            if (dayCell && !dayCell.classList.contains('fc-day-other')) {
              element.classList.add('selected-date')
            }
          }
        })
      }
    }
    
    // Update events section
    this.updateEventsSection(dateString)
    
    // Emit custom event
    this.dispatch('dateSelected', { detail: { date: dateString } })
  }

  initializeEventsSection() {
    // Load events for today by default
    const today = new Date()
    const dateString = today.toISOString().split('T')[0] // Format: YYYY-MM-DD
    
    // Set today as the initially selected date
    this.selectedDate = dateString
    
    this.loadEventsForDate(dateString)
  }

  updateEventsSection(dateString) {
    this.loadEventsForDate(dateString)
  }

  async loadEventsForDate(dateString) {
    if (!this.hasEventsTitleTarget || !this.hasEventsContentTarget) return
    
    try {
      // Fetch events for the specific date
      const response = await fetch(`/calendar/events?date=${dateString}`)
      const data = await response.json()
      
      // Parse date string for title
      const [year, month, day] = dateString.split('-').map(Number)
      const selectedDate = new Date(year, month - 1, day)
      const today = new Date()
      
      // Reset time components for comparison
      const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      
      // Set title
      if (selectedDateOnly.getTime() === todayOnly.getTime()) {
        this.eventsTitleTarget.textContent = 'Today'
      } else {
        const options = { weekday: 'long', month: 'long', day: 'numeric' }
        this.eventsTitleTarget.textContent = selectedDate.toLocaleDateString('en-US', options)
      }
      
      // Display events
      if (data.events && data.events.length > 0) {
        const eventsHtml = data.events.map(event => {
          switch(event.type) {
            case 'birthday':
              return `
                <div class="flex items-center gap-3 p-3 rounded-lg bg-base-200/50">
                  <div class="text-2xl">🎂</div>
                  <div>
                    <div class="font-medium">${event.title}</div>
                    ${event.age ? `<div class="text-sm text-base-content/60">${event.age} years old</div>` : ''}
                  </div>
                </div>
              `
            case 'work':
              const avatarContent = event.user_avatar 
                ? `<img src="${event.user_avatar}" alt="${event.user_first_name}" class="w-full h-full object-cover">`
                : `<div class="w-full h-full bg-primary text-primary-content flex items-center justify-center text-xs font-medium">${event.user_first_name.charAt(0)}</div>`
              
              const projectLinks = event.projects.map(project => 
                `<a href="/projects/${project.slug}" class="hover:text-primary hover:underline">${project.name}</a>`
              ).join(', ')
              
              return `
                <div class="flex items-center gap-2 p-2 rounded bg-primary/10 border border-primary/20">
                  <div class="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                    ${avatarContent}
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="font-medium text-sm">${event.title}</div>
                    <div class="text-xs text-base-content/60">${projectLinks}</div>
                  </div>
                </div>
              `
            case 'calendar_event':
              const eventTypeIcon = event.event_type === 'unavailability' ? '🚫' : '📅'
              const eventTypeBg = event.event_type === 'unavailability' ? 'bg-orange-500/10 border-orange-500/20' : 'bg-green-500/10 border-green-500/20'
              
              let timeInfo = ''
              if (event.spans_multiple_days) {
                const startDate = new Date(event.start_date)
                const endDate = new Date(event.end_date)
                const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                
                // For unavailability events, include user names
                if (event.event_type === 'unavailability' && event.assigned_user_names && event.assigned_user_names.length > 0) {
                  const userNames = event.assigned_user_names.join(', ')
                  timeInfo = `${userNames}, ${startStr} - ${endStr}`
                } else {
                  timeInfo = `${startStr} - ${endStr}`
                }
              } else {
                // Single day event - show user names if available, then time
                const timeStr = new Date(event.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                if (event.assigned_user_names && event.assigned_user_names.length > 0) {
                  const userNames = event.assigned_user_names.join(', ')
                  timeInfo = `${userNames}, ${timeStr}`
                } else {
                  timeInfo = timeStr
                }
              }
              
              const deleteButton = this.canManageEventsValue ? 
                `<button class="btn btn-ghost btn-xs text-error opacity-0 group-hover:opacity-100 transition-opacity" 
                         data-action="click->full-calendar#deleteEvent" 
                         data-event-id="${event.id}" 
                         title="Delete event">
                   <iconify-icon icon="ph:trash"></iconify-icon>
                 </button>` : ''

              return `
                <div class="flex items-center gap-3 p-3 rounded-lg ${eventTypeBg} group">
                  <div class="text-2xl">${eventTypeIcon}</div>
                  <div class="flex-1">
                    <div class="font-medium">${event.title}</div>
                    <div class="text-sm text-base-content/60">${timeInfo}</div>
                    ${event.description ? `<div class="text-xs text-base-content/50 mt-1">${event.description}</div>` : ''}
                  </div>
                  ${deleteButton}
                </div>
              `
            default:
              return `
                <div class="flex items-center gap-3 p-3 rounded-lg bg-base-200/50">
                  <div class="text-2xl">📅</div>
                  <div class="font-medium">${event.title}</div>
                </div>
              `
          }
        }).join('')
        
        this.eventsContentTarget.innerHTML = eventsHtml
      } else {
        this.eventsContentTarget.innerHTML = `
          <div class="flex flex-col items-center justify-center py-8 text-base-content/60">
            <iconify-icon icon="ph:calendar-x" class="text-4xl mb-2"></iconify-icon>
            <p>Nothing scheduled for this day</p>
          </div>
        `
      }
    } catch (error) {
      console.error('Failed to load events:', error)
      this.eventsContentTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-8 text-base-content/60">
          <iconify-icon icon="ph:warning" class="text-4xl mb-2"></iconify-icon>
          <p>Failed to load events</p>
        </div>
      `
    }
  }

  handleFilterChange(event) {
    const eventType = event.target.dataset.eventType
    const isChecked = event.target.checked
    
    // Add or remove CSS class to control visibility
    if (isChecked) {
      this.element.classList.remove(`hide-${eventType}`)
    } else {
      this.element.classList.add(`hide-${eventType}`)
    }
  }

  // Method to refresh the entire calendar after new events are created
  refresh() {
    // Refresh event indicators for the current month
    this.addEventIndicators()
    
    // Refresh events section with current selected date or today
    const selectedDateElement = this.calendarTarget.querySelector('.fc-daygrid-day-number.selected-date')
    let dateToRefresh = new Date().toISOString().split('T')[0] // Default to today
    
    if (selectedDateElement) {
      // Try to get the date from the selected element
      const dayCell = selectedDateElement.closest('.fc-daygrid-day')
      if (dayCell && dayCell.dataset.date) {
        dateToRefresh = dayCell.dataset.date
      }
    }
    
    this.loadEventsForDate(dateToRefresh)
  }

  // Method to open the event modal with pre-populated date
  openEventModal() {
    // Open the modal first
    calendar_events_modal.showModal()
    
    // Small delay to ensure modal is rendered before manipulating fields
    setTimeout(() => {
      // Pre-populate the start date field with selected date or today
      const dateToUse = this.selectedDate || new Date().toISOString().split('T')[0]
      
      // Convert date string to datetime-local format (YYYY-MM-DDTHH:MM)
      const dateObj = new Date(dateToUse + 'T09:00:00') // Default to 9 AM
      const formattedDateTime = dateObj.toISOString().slice(0, 16)
      
      // Find and populate the start date field (Rails generates form field IDs as model_attribute)
      const startDateField = document.querySelector('#calendar_event_start_date')
      if (startDateField) {
        startDateField.value = formattedDateTime
      }
      
      // Clear the end date field so it defaults to same day
      const endDateField = document.querySelector('#calendar_event_end_date')
      if (endDateField) {
        endDateField.value = ''
      }
    }, 100)
  }

  // Method to delete a calendar event
  async deleteEvent(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const eventId = event.currentTarget.dataset.eventId
    
    if (!confirm('Are you sure you want to delete this event?')) {
      return
    }
    
    try {
      const response = await fetch(`/calendar_events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
      
      if (response.ok) {
        // Process the turbo_stream response
        const responseText = await response.text()
        Turbo.renderStreamMessage(responseText)
      } else {
        console.error('Failed to delete event')
        alert('Failed to delete event. Please try again.')
      }
    } catch (error) {
      console.error('Error deleting event:', error)
      alert('Error deleting event. Please try again.')
    }
  }
} 